import type { Block, Cell, ChipCell, Column, Tone } from '@/kova/spec/types'

import type { ScreenData, ScreenLoader } from './provenance'
import {
  asArray, asRecord, attempt, disclosure, knows, malformed, readBool, readInstant,
  readInt, readText, receipt, refLine, summarise,
  type Attempt,
} from './wire'
import { exactMinor, isCurrencyCode } from '@/lib/money/money'

/**
 * Recognition — coverage, the feedback stream, the platform catalogue, the
 * orchestrator register and the retag jobs (this console #156).
 *
 *   rc-coverage   GET /v1/admin/recognition/coverage
 *   rc-feedback   GET /v1/admin/recognition/feedback
 *   rc-platforms  GET /v1/admin/platforms
 *   rc-orch       GET /v1/admin/orchestrators
 *   rc-retag      GET /v1/admin/retag-jobs
 *
 * # `rc-feedback` is the screen invariant 11 was written about
 *
 * User feedback NEVER mutates the payer catalog. It aggregates into catalog
 * candidates, and the only promotion path is the candidate queue with an
 * explicitly chosen platform. So this screen has no promote — and no DISMISS
 * either, because that "would be a write into the evidence table by the screen
 * that reads it".
 *
 * The corpus draws readable payer names. `payer_key` is PSEUDONYMOUS BY SCHEMA
 * CONSTRAINT and is not resolved back to a counterparty name or IBAN to make
 * the screen friendlier: that join is the thing the constraint exists to
 * prevent. An erased row is RETURNED AND MARKED rather than dropped, because a
 * queue that hid redacted rows would read as a smaller evidence base than it
 * is.
 *
 * # `rc-orch` renders two counts and never one total — invariant 11b
 *
 * A credit arriving through an orchestrator either resolved the platform
 * underneath or did not, and `orchestrator_only` is a REAL ANSWER rather than a
 * missing one. Adding them would report a route as fully attributed when half
 * its credits name nobody.
 *
 * `distinct_platforms` is rendered because more than one is the ordinary case,
 * and it is the reason a per-customer mapping can never be promoted to a global
 * one: promoting one customer's answer would attribute their income to another
 * customer paid by a different platform through the same route. This screen is
 * where somebody would reach for exactly that promotion, so the backend's own
 * sentence about it is rendered here.
 *
 * # Law 9 on `rc-coverage`
 *
 * Ratios are BASIS POINTS AS INTEGERS — 8500 is 85.00% — and are formatted from
 * the integer rather than converted to a float. Every row carries its own
 * currency and there is no total across them.
 *
 * # Invariant 10 — nothing here reads as an outage
 *
 * Recognition is never on the money path: a failure degrades a notification and
 * never blocks a credit. So an unrecognised credit and a failed retag job are
 * WORK, not incidents, and neither takes the critical tone. Critical is kept
 * for a read that failed, which is the console not knowing.
 *
 * # Read-only, all five
 *
 * `rc-retag` is the one where the missing control is loudest: applying a retag
 * supersedes decisions and obliges a NEW STATEMENT VERSION for every affected
 * period (invariant 12) — customer-visible and irreversible once issued — "so
 * it is an instruction somebody authorises rather than a button beside a
 * count".
 */

export const COVERAGE_PATH = '/recognition/coverage'
export const FEEDBACK_PATH = '/recognition/feedback'
export const PLATFORMS_PATH = '/platforms'
export const ORCHESTRATORS_PATH = '/orchestrators'
export const RETAG_JOBS_PATH = '/retag-jobs'

const COVERAGE_SOURCE = 'postgres · recognition_decisions (GET /v1/admin/recognition/coverage)'
const FEEDBACK_SOURCE = 'postgres · recognition_feedback (GET /v1/admin/recognition/feedback)'
const PLATFORMS_SOURCE = 'postgres · platforms (GET /v1/admin/platforms)'
const ORCH_SOURCE = 'postgres · recognition_decisions (GET /v1/admin/orchestrators)'
const RETAG_SOURCE = 'postgres · retag_jobs (GET /v1/admin/retag-jobs)'

/** Our own catalogue and our own decisions. No third party asserts any of it. */
const AUTHORITY = 'operational'

function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  return {
    blocks: [{
      t: 'note', tone: 'r', kicker, title,
      body: `${failed.reason} ${body}`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

/**
 * A ratio held as basis points, rendered from the INTEGER.
 *
 * 8500 is 85.00%. Never `value / 100` into a float and never `toFixed` — law 9,
 * and the backend keeps them as integers for exactly this reason. The two
 * decimal places here are basis-point arithmetic on an integer, not a currency
 * scale, so this is not the hardcoded-two-decimals law 8 forbids.
 */
function basisPoints(bp: number): string {
  const sign = bp < 0 ? '-' : ''
  const abs = Math.abs(Math.trunc(bp))
  return `${sign}${String(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, '0')} %`
}

/** An amount cell from a bare minor field plus a sibling currency. */
function minorCell(raw: unknown, currency: unknown): Cell {
  const minor = exactMinor(raw)
  if (minor === null) return '— not a figure this console can state'
  if (!isCurrencyCode(currency)) return '— no currency on this amount'
  return [minor, currency.trim().toUpperCase()]
}

// ===========================================================================
// rc-coverage
// ===========================================================================

const COVERAGE_COLS: readonly Column[] = [
  ['Group', 'x', 'minmax(180px,1.3fr)'],
  ['Currency', 'm', '96px'],
  ['Decided', 'n', '110px'],
  ['Recognised', 'n', '120px'],
  ['Coverage', 'n', '120px'],
  ['Value decided', 'a', '160px'],
  ['Of which unrecognised', 'a', '180px'],
]

export function composeCoverage(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Recognition coverage', 'Coverage could not be read',
      'No figure below is known — that is different from nothing having been decided.',
      read, COVERAGE_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['rows'])
  if (o === null || list === null) {
    return unread(
      'Recognition coverage', 'Coverage could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), COVERAGE_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const currencies = new Set<string>()
  let unreadable = 0

  for (const raw of list) {
    const r = asRecord(raw)
    if (r === null) { unreadable += 1; continue }

    const group = readText(r, 'group')
    if (group === null) gaps.push('a group name')
    const currency = readText(r, 'currency')
    if (currency === null || !isCurrencyCode(currency)) gaps.push('a currency on a coverage row')
    else currencies.add(currency.toUpperCase())

    const decided = readInt(r, 'decided_count')
    if (decided.kind !== 'exact') gaps.push('a decided count')
    const recognised = readInt(r, 'recognised_count')
    if (recognised.kind !== 'exact') gaps.push('a recognised count')

    // Basis points as an integer, rendered as one. Never a float.
    const bp = readInt(r, 'ratio_basis_points')
    if (bp.kind !== 'exact') gaps.push('a coverage ratio')

    rows.push([
      group ?? '— unnamed',
      currency !== null && isCurrencyCode(currency) ? currency.toUpperCase() : '— none',
      decided.kind === 'exact' ? String(decided.value) : '—',
      recognised.kind === 'exact' ? String(recognised.value) : '—',
      bp.kind === 'exact' ? basisPoints(bp.value) : '—',
      minorCell(r['total_minor'], currency),
      minorCell(r['unrecognised_minor'], currency),
    ])
  }

  const from = readInstant(o, 'from')
  const to = readInstant(o, 'to')
  const groupBy = readText(o, 'group_by')
  const ever = readInt(o, 'decisions_ever_recorded')

  // An empty window and a system that has never decided anything are different
  // facts. The backend derives which from the population rather than asserting
  // it, and its sentence is preferred where it sent one.
  const emptyNote = readText(o, 'empty_note')
  const footer = rows.length === 0
    ? (emptyNote ?? (ever.kind === 'exact' && ever.value === 0
        ? 'No recognition decision has ever been recorded. This window is empty because nothing has run, not because nothing matched.'
        : 'No decision falls in this window.'))
    : `${String(rows.length)} ${rows.length === 1 ? 'group' : 'groups'}${groupBy === null ? '' : ` by ${groupBy}`}${currencies.size > 1 ? ` · across ${String(currencies.size)} currencies, which is why there is no total` : ''}${from.kind === 'at' && to.kind === 'at' ? ` · ${from.label} → ${to.label}` : ''}`

  const blocks: Block[] = [{
    t: 'table', title: 'Coverage by group', src: 'ledger', facets: false,
    cols: COVERAGE_COLS, rows, footer,
  }]

  const ratioNote = disclosure(o, 'ratio_note', 'Ratios', 'How the coverage figure is held', 'n')
  if (ratioNote !== null && rows.length > 0) blocks.push(ratioNote)
  const currencyNote = disclosure(o, 'currency_note', 'Currency', 'There is no total across currencies', 'n')
  if (currencyNote !== null && rows.length > 0) blocks.push(currencyNote)

  const missing = [
    'a total across currencies — every row carries its own and a sum across them is not a figure',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} coverage row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: COVERAGE_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Recognition coverage',
    subject: `recognition_decisions · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadCoverage: ScreenLoader = async (api) =>
  composeCoverage(await attempt(() => api.get<unknown>(COVERAGE_PATH), 'Recognition coverage'), new Date())

// ===========================================================================
// rc-feedback — no promote, no dismiss, no payer name
// ===========================================================================

/**
 * Feedback kinds.
 *
 * None is critical. A customer correcting a payer attribution is ordinary
 * evidence, not an incident — invariant 10, and the reason the section's
 * screens must not read as outages.
 */
const FEEDBACK_TONE: Readonly<Record<string, Tone>> = {
  confirm: 'g',
  correct: 'a',
  unknown_payer: 'a',
  dispute: 'a',
}

const FEEDBACK_COLS: readonly Column[] = [
  ['Feedback', 'm', '96px'],
  ['Decision', 'm', '104px'],
  ['Kind', 'c', '150px'],
  ['Platform named', 'm', '150px'],
  ['Payer key', 'm', 'minmax(170px,1.2fr)'],
  ['Given by', 'x', '140px'],
  ['At', 'm', '150px'],
  ['Standing', 'c', '150px'],
]

export function composeFeedback(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Recognition feedback', 'The feedback stream could not be read',
      'No entry below is known — that is different from customers having said nothing.',
      read, FEEDBACK_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['feedback'])
  if (o === null || list === null) {
    return unread(
      'Recognition feedback', 'The feedback stream could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), FEEDBACK_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let redacted = 0

  for (const raw of list) {
    const f = asRecord(raw)
    if (f === null) { unreadable += 1; continue }

    const id = readInt(f, 'id')
    const decision = readInt(f, 'decision_id')
    if (decision.kind !== 'exact') gaps.push('the decision a note is about')

    const kind = readText(f, 'kind')
    if (kind === null) gaps.push('a feedback kind')
    const kindCell: ChipCell = kind === null
      ? ['unstated', 'a']
      : [kind, knows(FEEDBACK_TONE, kind) ? (FEEDBACK_TONE[kind] ?? 'a') : 'a']

    // `platform_id` is `omitempty` — a customer saying "this is not mine"
    // names no platform, which is a position rather than a gap.
    const platform = readText(f, 'platform_id')

    // PSEUDONYMOUS BY SCHEMA CONSTRAINT, and rendered as the opaque key it is.
    // It is never resolved to a counterparty name or IBAN to make the screen
    // friendlier: that join is what the constraint exists to prevent.
    const payerKey = readText(f, 'payer_key')
    if (payerKey === null) gaps.push('a payer key')

    // An erased row is RETURNED AND MARKED, never dropped: a queue that hid
    // them would read as a smaller evidence base than it is.
    const isRedacted = readBool(f, 'redacted')
    if (isRedacted === true) redacted += 1

    const created = readInstant(f, 'created_at')

    rows.push([
      id.kind === 'exact' ? String(id.value) : '— no id',
      decision.kind === 'exact' ? String(decision.value) : '—',
      kindCell,
      platform ?? '— names no platform',
      payerKey ?? '— none',
      readText(f, 'actor') ?? '— unattributed',
      created.kind === 'at' ? created.label : '—',
      isRedacted === true
        ? ['erased under a privacy request', 'a']
        : isRedacted === false ? ['current', 'n'] : ['unstated', 'a'],
    ])
  }

  const hasMore = readBool(o, 'has_more') === true
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No feedback in this window. Customers correcting an attribution appear here; promotion into the catalogue happens on the candidate queue.'
        : `No entry could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'entry' : 'entries'}${redacted === 0 ? '' : ` · ${String(redacted)} erased and kept`}${hasMore ? ' · the backend caps the page' : ''}`

  const blocks: Block[] = [{
    t: 'table', title: 'What customers told us', src: 'ledger', facets: false,
    cols: FEEDBACK_COLS, rows, footer,
  }]

  // The invariant, in the backend's own words, on the screen it governs.
  const controlNote = disclosure(
    o, 'control_note', 'Promotion', 'This screen does not change the catalogue', 'n',
  )
  if (controlNote !== null) blocks.push(controlNote)

  const payerNote = disclosure(o, 'payer_key_note', 'Payer key', 'Why this is not a name', 'n')
  if (payerNote !== null && rows.length > 0) blocks.push(payerNote)

  const missing = [
    'a promote action — customer feedback never mutates the payer catalog; promotion happens only from the candidate queue, with a platform chosen explicitly',
    'a dismiss action — that would be a write into the evidence table by the screen that reads it',
    'the payer’s name or IBAN — the payer key is pseudonymous by schema constraint and is not resolved back to one here',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} feedback row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: FEEDBACK_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Recognition feedback',
    subject: `recognition_feedback · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadFeedback: ScreenLoader = async (api) =>
  composeFeedback(await attempt(() => api.get<unknown>(FEEDBACK_PATH), 'The feedback stream'), new Date())

// ===========================================================================
// rc-platforms — the catalogue nothing in the codebase writes
// ===========================================================================

const PLATFORM_COLS: readonly Column[] = [
  ['Platform', 'm', '150px'],
  ['Name', 'x', 'minmax(180px,1.3fr)'],
  ['Kind', 'c', '130px'],
  ['Segment', 'c', '150px'],
  ['Live catalogue entries', 'n', '170px'],
  ['Payout guidance', 'n', '150px'],
  ['Added', 'm', '150px'],
]

/** A platform kind is descriptive, so none of these is an alarm. */
const PLATFORM_KIND_TONE: Readonly<Record<string, Tone>> = {
  platform: 'n', orchestrator: 'a', marketplace: 'n', psp: 'n',
}

function platformRow(p: Record<string, unknown>, gaps: string[]): readonly Cell[] {
  const id = readText(p, 'id')
  if (id === null) gaps.push('a platform id')
  const kind = readText(p, 'kind')
  if (kind === null) gaps.push('a platform kind')

  // The operator's own risk fact, and this is the only surface it appears on.
  const segment = readText(p, 'segment')

  const entries = readInt(p, 'live_entry_count')
  if (entries.kind !== 'exact') gaps.push('a live entry count')
  const langs = readInt(p, 'guidance_langs')
  const created = readInstant(p, 'created_at')

  return [
    id ?? '— unnamed',
    readText(p, 'display_name') ?? '—',
    kind === null ? ['unstated', 'a'] : [kind, knows(PLATFORM_KIND_TONE, kind) ? (PLATFORM_KIND_TONE[kind] ?? 'a') : 'a'],
    segment === null ? ['— none recorded', 'n'] : [segment, 'n'],
    // A real zero: a platform in the catalogue that no entry resolves. Distinct
    // from an unread one, which is a dash.
    entries.kind === 'exact' ? String(entries.value) : '—',
    langs.kind === 'exact' ? String(langs.value) : '—',
    created.kind === 'at' ? created.label : '—',
  ]
}

export function composePlatforms(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Platform catalogue', 'The catalogue could not be read',
      'No platform below is known — that is different from none being registered.',
      read, PLATFORMS_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['platforms'])
  if (o === null || list === null) {
    return unread(
      'Platform catalogue', 'The catalogue could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), PLATFORMS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let unsourced = 0
  for (const raw of list) {
    const p = asRecord(raw)
    if (p === null) { unreadable += 1; continue }
    rows.push(platformRow(p, gaps))
    const entries = readInt(p, 'live_entry_count')
    if (entries.kind === 'exact' && entries.value === 0) unsourced += 1
  }

  const hasMore = readBool(o, 'has_more') === true
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No platform is in the catalogue. Nothing in this codebase writes this table — rows arrive by migration, so an empty catalogue means none has been seeded.'
        : `No platform could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'platform' : 'platforms'}${unsourced === 0 ? '' : ` · ${String(unsourced)} with no live catalogue entry resolving to them`}${hasMore ? ' · the backend caps the page' : ''}`

  const blocks: Block[] = [{
    t: 'table', title: 'Platforms', src: 'ledger', facets: false,
    cols: PLATFORM_COLS, rows, footer,
  }]

  const maintenance = disclosure(o, 'maintenance_note', 'Maintenance', 'How this table is written', 'n')
  if (maintenance !== null) blocks.push(maintenance)
  const segment = disclosure(o, 'segment_note', 'Segment', 'What the segment column is', 'n')
  if (segment !== null && rows.length > 0) blocks.push(segment)

  const missing = [
    'any control — no Go code writes this table, so there is nothing here to create, edit or retire a platform with',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} platform(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: PLATFORMS_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Platform catalogue',
    subject: `platforms · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadPlatforms: ScreenLoader = async (api) =>
  composePlatforms(await attempt(() => api.get<unknown>(PLATFORMS_PATH), 'The platform catalogue'), new Date())

// ===========================================================================
// rc-orch — two counts, never one total (invariant 11b)
// ===========================================================================

/**
 * The columns.
 *
 * `Attributed` and `Orchestrator only` are TWO columns and there is no third
 * summing them. A credit through an orchestrator either resolved the platform
 * underneath or did not, and `orchestrator_only` is a real answer rather than a
 * missing one — adding them would report a route as fully attributed when half
 * its credits name nobody.
 *
 * `Platforms seen` is rendered because more than one is the ordinary case, and
 * it is the reason a per-customer mapping can never be promoted to a global
 * one.
 */
const ORCH_COLS: readonly Column[] = [
  ['Orchestrator', 'm', '150px'],
  ['Name', 'x', 'minmax(170px,1.2fr)'],
  ['Segment', 'c', '150px'],
  ['Credits attributed to a platform', 'n', '200px'],
  ['Credits naming only the route', 'n', '200px'],
  ['Platforms seen underneath', 'n', '190px'],
]

export function composeOrchestrators(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Orchestrators', 'The orchestrator register could not be read',
      'No route below is known — that is different from none being in use.',
      read, ORCH_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['orchestrators'])
  if (o === null || list === null) {
    return unread(
      'Orchestrators', 'The orchestrator register could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), ORCH_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let multiPlatform = 0

  for (const raw of list) {
    const r = asRecord(raw)
    if (r === null) { unreadable += 1; continue }

    const id = readText(r, 'id')
    if (id === null) gaps.push('an orchestrator id')

    const attributed = readInt(r, 'attributed_credits')
    if (attributed.kind !== 'exact') gaps.push('an attributed-credit count')
    // A REAL ANSWER, not a missing one — and never added to the other.
    const routeOnly = readInt(r, 'orchestrator_only_credits')
    if (routeOnly.kind !== 'exact') gaps.push('an orchestrator-only credit count')

    const distinct = readInt(r, 'distinct_platforms')
    if (distinct.kind !== 'exact') gaps.push('a distinct-platform count')
    if (distinct.kind === 'exact' && distinct.value > 1) multiPlatform += 1

    const segment = readText(r, 'segment')

    rows.push([
      id ?? '— unnamed',
      readText(r, 'display_name') ?? '—',
      segment === null ? ['— none recorded', 'n'] : [segment, 'n'],
      attributed.kind === 'exact' ? String(attributed.value) : '—',
      routeOnly.kind === 'exact' ? String(routeOnly.value) : '—',
      distinct.kind === 'exact' ? String(distinct.value) : '—',
    ])
  }

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No orchestrator has carried a credit. These are routes a payment travelled through, so an empty register means none has been seen.'
        : `No route could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    // Two counts, and the footer does not add them either.
    : `${String(rows.length)} ${rows.length === 1 ? 'route' : 'routes'} · the two credit counts are separate figures and are never summed`

  const blocks: Block[] = [{
    t: 'table', title: 'Orchestrators', src: 'ledger', facets: false,
    cols: ORCH_COLS, rows, footer,
  }]

  const splitNote = disclosure(o, 'split_note', 'The split', 'Why there are two counts', 'a')
  if (splitNote !== null && rows.length > 0) blocks.push(splitNote)

  // Invariant 11b, on the screen where somebody would reach for the promotion.
  const perCustomer = disclosure(
    o, 'per_customer_note', 'Per customer', 'These are global facts, not per-customer ones', 'a',
  )
  if (perCustomer !== null) blocks.push(perCustomer)

  if (multiPlatform > 0) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Why mappings stay per customer',
      title: `${String(multiPlatform)} route${multiPlatform === 1 ? ' has' : 's have'} carried more than one platform`,
      body: 'Two customers paid through the same route can be paid by different platforms, so one customer’s answer is not a fact about the route. There is no promotion from here.',
      ref: 'distinct_platforms > 1',
    })
  }

  const missing = [
    'a per-customer mapping — which platform pays a given customer through a route is per-customer state, is not readable on this rail, and may not be promoted from here',
    'a combined credit total — the two counts are separate answers and summing them would report a route as fully attributed when half its credits name nobody',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} route(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: ORCH_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Orchestrators',
    subject: `orchestrators · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadOrchestrators: ScreenLoader = async (api) =>
  composeOrchestrators(await attempt(() => api.get<unknown>(ORCHESTRATORS_PATH), 'The orchestrator register'), new Date())

// ===========================================================================
// rc-retag — four states, none of which means anything was retagged
// ===========================================================================

/**
 * Job states.
 *
 * NONE of them means anything was retagged, and the labels say so rather than
 * leaving `previewed` or `applied` to be read as done. `failed` is attention
 * and not critical: recognition is off the money path (invariant 10), so a
 * failed retag degrades a tag and blocks no credit.
 */
const RETAG_LABEL: Readonly<Record<string, readonly [string, Tone]>> = {
  opened: ['opened · nothing scanned yet', 'n'],
  previewing: ['previewing · scanning, changing nothing', 'n'],
  previewed: ['previewed · a proposal, not applied', 'a'],
  failed: ['failed · nothing was changed', 'a'],
}

const RETAG_COLS: readonly Column[] = [
  ['Job', 'm', '90px'],
  ['Trigger', 'm', 'minmax(180px,1.3fr)'],
  ['Platform', 'x', '150px'],
  ['Scope', 'm', 'minmax(180px,1.2fr)'],
  ['State', 'c', '210px'],
  ['Decisions scanned', 'n', '160px'],
  ['Tags that would change', 'n', '180px'],
  ['Statements owed a new version', 'n', '210px'],
  ['Opened by', 'x', '140px'],
]

export function composeRetagJobs(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Retag jobs', 'The retag jobs could not be read',
      'No job below is known — that is different from no catalogue correction having been made.',
      read, RETAG_SOURCE,
    )
  }
  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['jobs'])
  if (o === null || list === null) {
    return unread(
      'Retag jobs', 'The retag jobs could not be read',
      'The read returned a shape this console does not know.',
      malformed('The read returned a shape this console does not know.'), RETAG_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let statementsOwed = 0

  for (const raw of list) {
    const j = asRecord(raw)
    if (j === null) { unreadable += 1; continue }

    const id = readInt(j, 'id')
    const entryKey = readText(j, 'trigger_entry_key')
    const entryVersion = readInt(j, 'trigger_entry_version')
    if (entryKey === null) gaps.push('the catalogue entry a job was triggered by')

    const state = readText(j, 'state')
    if (state === null) gaps.push('a job state')
    const known = state !== null && knows(RETAG_LABEL, state)
    if (state !== null && !known) gaps.push('a job state this console recognises')
    const label = known ? RETAG_LABEL[state] : undefined
    const stateCell: ChipCell = [label?.[0] ?? (state ?? 'unstated'), label?.[1] ?? 'a']

    const scanned = readInt(j, 'decisions_scanned')
    // COUNTS TAGS, NOT PROVENANCE — the column is labelled as what it counts.
    const changed = readInt(j, 'decisions_changed')
    const statements = readInt(j, 'statements_affected')
    if (statements.kind === 'exact' && statements.value > 0) statementsOwed += statements.value

    const from = readInstant(j, 'scope_from')
    const to = readInstant(j, 'scope_to')

    rows.push([
      id.kind === 'exact' ? String(id.value) : '— no id',
      entryKey === null
        ? '— not stated'
        : entryVersion.kind === 'exact' ? `${entryKey} v${String(entryVersion.value)}` : entryKey,
      readText(j, 'platform_name') ?? readText(j, 'platform_id') ?? '—',
      from.kind === 'at' && to.kind === 'at' ? `${from.label} → ${to.label}` : '—',
      stateCell,
      scanned.kind === 'exact' ? String(scanned.value) : '—',
      changed.kind === 'exact' ? String(changed.value) : '—',
      // Absent rather than zero until a preview has run.
      statements.kind === 'exact' ? String(statements.value) : '— not previewed',
      readText(j, 'opened_by') ?? '— unattributed',
    ])
  }

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No retag job. A job exists for every catalogue correction and for nothing else, so an empty list means no correction has been made.'
        : `No job could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'job' : 'jobs'} · no state here means anything was retagged`

  const blocks: Block[] = [{
    t: 'table', title: 'Retag jobs', src: 'ledger', facets: false,
    cols: RETAG_COLS, rows, footer,
  }]

  const stateNote = disclosure(o, 'state_note', 'States', 'None of these means anything was retagged', 'a')
  if (stateNote !== null && rows.length > 0) blocks.push(stateNote)
  const changedNote = disclosure(o, 'changed_note', 'What is counted', 'Tags, not provenance', 'n')
  if (changedNote !== null && rows.length > 0) blocks.push(changedNote)

  // Invariant 12: applying a retag obliges a NEW statement version for every
  // affected period — customer-visible and irreversible once issued. That is
  // the sentence that explains why there is no apply button.
  if (statementsOwed > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Statements',
      title: `${String(statementsOwed)} issued statement${statementsOwed === 1 ? '' : 's'} would be owed a new version`,
      body: 'Applying a correction supersedes the decisions those statements cite, and a new version is owed for every affected period. That is customer-visible and irreversible once issued.',
      ref: 'statements_affected · invariant 12',
    })
  }

  const missing = [
    'a start, apply or cancel control — applying a retag supersedes decisions and obliges a new statement version for every affected period, so it is an instruction somebody authorises rather than a button beside a count',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} job(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: RETAG_SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Retag jobs',
    subject: `retag_jobs · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadRetagJobs: ScreenLoader = async (api) =>
  composeRetagJobs(await attempt(() => api.get<unknown>(RETAG_JOBS_PATH), 'The retag jobs'), new Date())
